import React, {useCallback, useRef, useState} from "react";
import {Link as RouterLink, useNavigate, useParams} from "react-router-dom";
import useApiCall, {makeApiCall, UseApiCallResponseData} from "../../hooks/CancellableApiCall";
import {InternalAffairsComplaintResponse} from "../../data/internal-affairs/InternalAffairsComplaintResponse";
import {LoadingContent} from "../../components/LoadingContent";
import {InternalAffairsComplaintActivityResponse} from "../../data/internal-affairs/InternalAffairsComplaintActivityResponse";
import {
    Alert,
    Box,
    Button,
    Divider,
    Grid,
    Link,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Avatar,
    IconButton
} from "@mui/material";
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineItem,
    timelineItemClasses,
    TimelineOppositeContent,
    TimelineSeparator
} from '@mui/lab';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import {formatTimestamp} from "../../utils";
import {TextEditor} from "../../components/editor/TextEditor";
import {$getRoot, LexicalEditor} from "lexical";
import {$generateHtmlFromNodes} from '@lexical/html';
import {EditorContent} from "../../components/editor/EditorContent";
import {
    InternalAffairsComplaintInvestigatorResponse
} from "../../data/internal-affairs/InternalAffairsComplaintInvestigatorResponse";
import useUser from "../../hooks/useUser";
import {AccountResponse} from "../../data/AccountResponse";
import {MessageBoxWithUserDetails} from "../../components/MessageBoxWithUserDetails";
import {DivisionRoles} from "../../data/DivisionRoles";
import {InternalAffairsStatementResponse} from "../../data/internal-affairs/InternalAffairsStatementResponse";
import InternalAffairsNewStatementForm from "../../components/internal-affairs/forms/InternalAffairsNewStatementForm";
import InternalAffairsStatementViewForm from "../../components/internal-affairs/forms/InternalAffairsStatementViewForm";
import InternalAffairsStatementSubmitForm from "../../components/internal-affairs/forms/InternalAffairsStatementSubmitForm";
import {
    InternalAffairsComplaintStatus,
    internalAffairsComplaintStatus
} from "../../data/internal-affairs/InternalAffairsComplaintStatus";
import {
    internalAffairsComplaintClassification,
    InternalAffairsComplaintClassification
} from "../../data/internal-affairs/InternalAffairsComplaintClassification";
import {InternalAffairsComplaintOutcome} from "../../data/internal-affairs/InternalAffairsComplaintOutcome";
import {AlertType} from "../../data/AlertType";
import {ConfirmationDialog} from "../../components/ConfirmationDialog";
import {IACMDReview, iaCommandDecision} from "../../components/internal-affairs/banners/IACMDReview";
import {JuniorCCReview} from "../../components/internal-affairs/banners/JuniorCCReview";
import {SeniorCCReview} from "../../components/internal-affairs/banners/SeniorCCReview";
import {Policy} from "@mui/icons-material";

interface complaint {
    complaint: InternalAffairsComplaintResponse,
    complaintApiCall: UseApiCallResponseData<InternalAffairsComplaintResponse>
    investigators: InternalAffairsComplaintInvestigatorResponse[],
    user: AccountResponse,
    userIARoleDisplayName: string
}

interface investigator {
    investigatorIndex: number,
    complaint: InternalAffairsComplaintResponse
}

interface StatementRowProps {
    statement: InternalAffairsStatementResponse
    user: AccountResponse
}

export const StatementRow = (props: StatementRowProps) => {
    let option
    if (props.statement.status === "Pending Response" && props.statement.providerCommunityId === props.user.communityId) {
        option = <InternalAffairsStatementSubmitForm statement={props.statement}/>
    } else {
        option = <InternalAffairsStatementViewForm statement={props.statement}/>
    } if (props.statement.complaintOutcome !== null && props.statement.status === "Pending Response" && props.statement.providerCommunityId === props.user.communityId) {
        option = <Button disabled={props.statement.complaintOutcome !== null}>Complaint Closed</Button>
    }

    return <TableRow>
        <TableCell> {props.statement.id} </TableCell>
        <TableCell> <Link underline="none"
                          component={RouterLink}
                          to={`/user/${props.statement.providerCommunityId}`}
        > {props.statement.providerRpName} ({props.statement.providerNick}) </Link>
        </TableCell>
        <TableCell> {formatTimestamp(props.statement.date)} </TableCell>
        <TableCell> {props.statement.status} </TableCell>
        <TableCell> {option} </TableCell>
    </TableRow>
}

function InternalAffairsComplaintEvent(props: {data: InternalAffairsComplaintActivityResponse}) {
    return <TimelineItem>
            <TimelineSeparator>
            <Link sx={{cursor: 'default'}} underline="none" component={RouterLink} to={`/user/${props.data.communityId}`}>
                <Avatar
                    src={props.data.avatar}
                />
            </Link>
                <TimelineConnector
                    sx={{
                        backgroundColor: "rgba(182,182,192,0.45)"
                    }}
                />
            </TimelineSeparator>
            <TimelineContent sx={{px: 2, mt: "-7px", mb: "40px"}}>
                <Stack direction="column">
                    <Typography variant="body1" component="span">
                        {props.data.nick}
                    </Typography>
                </Stack>
                <Stack direction="column">
                    <Typography variant="body1" component="span">
                        {props.data.message}
                    </Typography>
                </Stack>
            </TimelineContent>
            <TimelineOppositeContent>
                <Stack sx={{pt: 1}} direction="row">
                    <Typography color={"secondary"} variant="caption">
                        {formatTimestamp(props.data.date)}
                    </Typography>
                </Stack>
            </TimelineOppositeContent>
        </TimelineItem>
}

function Complaint(props: complaint) {
    const [complaintStatus, setComplaintStatus] = useState(InternalAffairsComplaintStatus.get(props.complaint.status)?.key ?? "closed")
    const [complaintAction, setComplaintAction] = useState(InternalAffairsComplaintOutcome.get(props.complaint.outcome ?? "pending")?.displayName)
    const [complaintClassification, setComplaintClassification] = useState(InternalAffairsComplaintClassification.get(props.complaint.classification ?? "pending")?.key)
    const [assignedInvestigators, setAssignedInvestigators] = useState(props.complaint.assignees)
    const [complaintMessages, setComplaintMessages] = useState(props.complaint.messages)
    const [complaintCreator, setComplaintCreator] = useState(props.complaint.creatorName)
    const [complaintCreatorCommunityId, setComplaintCreatorCommunityId] = useState(props.complaint.creatorCommunityId)
    const [showAlert, setShowAlert] = useState(false)
    const [alertType, setAlertType] = useState<AlertType>("success")
    const [alertText, setAlertText] = useState("")
    const [selectedInvestigatorIndex, setSelectedInvestigatorIndex] = useState<number>(0)
    const [viewHiddenIAWarning, setViewHiddenIAWarning] = useState(false)
    const [complaintDueDate, setComplaintDueDate] = useState(props.complaint.dueDate ? new Date(props.complaint.dueDate).toISOString().slice(0, 16) : "")
    const editorRef = useRef<LexicalEditor>(null)
    const navigate = useNavigate()

    const setAlert = useCallback((type: AlertType, text: string) => {
        setAlertType(type)
        setAlertText(text)
        setShowAlert(true)
    }, [setAlertType, setAlertText, setShowAlert])

    const viewIACreator = (confirm: boolean) => {
        if (confirm) {
            makeApiCall({
                url: `/api/internal-affairs/creator/${props.complaint.displayId}`,
                method: "GET",
                onLoadedCallback: (data: {creatorName: string, creatorCommunityId: string}) => {
                    setAlert("success", "Creator has been added to the complaint details section")
                    setComplaintCreator(data.creatorName)
                    setComplaintCreatorCommunityId(data.creatorCommunityId)
                },
                onError: () => {
                    setAlert("error", "Unable to fetch IA Creator")
                }
            })
        }
        setViewHiddenIAWarning(false)
    }

    let finalReportButton

    let userIaRank = ``
    if (props.userIARoleDisplayName !== "") {
        userIaRank = `<p class="editor-paragraph" dir="ltr" style="text-align: start;"><span style="white-space: pre-wrap;">${props.userIARoleDisplayName}</span></p>`
    }

    const initialContent = (props.complaint.creatorCommunityId !== props.user.communityId ? `<p class="editor-paragraph" dir="ltr" style="text-align: start;"><span style="white-space: pre-wrap;">Hello,</span></p><p class="editor-paragraph" dir="ltr" style="text-align: start;"><br></p><p class="editor-paragraph" dir="ltr" style="text-align: start;"><span style="white-space: pre-wrap;">Kind Regards,</span></p><p class="editor-paragraph" dir="ltr" style="text-align: start;"><span style="white-space: pre-wrap;">${props.user.rank?.displayName} ${props.user.rpName}</span></p>${userIaRank}` : '')

    if ((InternalAffairsComplaintStatus.get(complaintStatus)?.statusOrder ?? -1 >= 5) && complaintClassification !== "pending") {
        finalReportButton = <>
            <Button color="info"
                    disabled={props.user.communityId === props.complaint.creatorCommunityId && !props.complaint.complaintPermissions?.includes("view_final_report") }
                    component={RouterLink} to={`/final-report/${props.complaint.displayId ?? props.complaint.displayId}`}>View Final
                Report</Button><Divider/>
        </>
    } else if (!props.complaint.complaintPermissions?.includes("submit_final_report") || complaintClassification === "pending") {
        finalReportButton = <>
            <Tooltip title="You need to change the complaints classification first">
              <center><span>
                <Button disabled>Write Final Report</Button>
              </span></center>
            </Tooltip>
            <Divider/>
        </>
    } else {
        finalReportButton = <>
            <Button color="success"
                    component={RouterLink} to={`/final-report/${props.complaint.displayId}`}>
                Write Final Report
            </Button>
            <Divider/>
        </>
    }

    let statementView = <></>
    if (props.complaint.complaintPermissions?.includes("view_statements")) {
        statementView = <>
            <Typography sx={{pt:2}} variant="h6" gutterBottom>Statements</Typography>
            <Paper variant={"outlined"} style={{
                padding: "16px"
            }}>
                <Box sx={{overflowX:"auto"}}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Statement ID</TableCell>
                                <TableCell>Provider Community ID</TableCell>
                                <TableCell>Date last Updated</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell> </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {props.complaint.statements && props.complaint.statements.map((statement) => {
                                return <StatementRow key={statement.id} user={props.user} statement={statement}/>
                            })}
                        </TableBody>
                    </Table>
                </Box>
                <InternalAffairsNewStatementForm defaultTarget={props.complaint.defendantCommunityId}
                                                 displayId={props.complaint.displayId} parentApiCall={props.complaintApiCall}/>
            </Paper>
        </>
    }

    let IABanner = <></>
    if ((props.user.permissions?.includes("internalAffairs:complaintSeniorReviewBoard") || props.user.permissions?.includes("internalAffairs:complaintJuniorReviewBoard")) && props.complaint.complaintPermissions?.includes("view_final_report")) {
    switch (complaintStatus) {
        case "ia_cmd_review": {
            IABanner = <IACMDReview
                complaint={props.complaint}
                onSuccess={(type: iaCommandDecision) => {
                    switch (type) {
                        case "CC_MEETING":
                            setComplaintStatus("final_reported")
                            setAlert("success", "Complaint Sent to CC Meeting")
                            break
                        case "CLOSE":
                            setComplaintStatus("closed")
                            setComplaintAction("Withdrawn")
                            setAlert("success", "Complaint Closed")
                            break
                        case "CC_QUICK_VOTE":
                            setComplaintStatus("junior_cc_review")
                            setAlert("success", "Complaint Sent to CC Voting")
                            break
                    }
                }}
                onFail={() => {
                    setAlert("error", "Failed to Perform Action")
                }}
            />
            break
        }

        case "junior_cc_review": {
            IABanner = <JuniorCCReview
                complaint={props.complaint}
                onSuccess={(type: iaCommandDecision) => {
                    setAlert("error", "Failed to Perform Action")
                }}
                onFail={() => {
                   setAlert("error", "Failed to Perform Action")
                }}
            />
            break
        }

        case "senior_cc_review": {
            IABanner = <SeniorCCReview complaint={props.complaint}/>
            break
        }

        default: {}
    }
}

    const updateClassification = (complaintId: string, classification: string) => {
        makeApiCall({
            url: `/api/internal-affairs/complaint/${complaintId}/classification`,
            method: "POST",
            body: {
                classification: classification
            },
            onLoadedCallback: () => {
                setAlert("success", "Classification Update")
                setComplaintClassification(classification)
            },
            onError: () => {
                setAlert("error", "Classification failed to update due to a issue")
            }
        })
    }

    const updateStatus = (complaintId: string, status: string) => {
        makeApiCall({
            url: `/api/internal-affairs/complaint/${complaintId}/status`,
            method: "POST",
            body: {
                status: status
            },
            onLoadedCallback: () => {
                setAlert("success", "Status Update")
                setComplaintStatus(status)
            },
            onError: () => {
                setAlert("error", "Status failed to update due to a issue")
            }
        })
    }

    const assignInvestigator = (complaintId: string, investigatorIndex: number) => {
        let investigator = props.investigators.filter(investigator => {
            return !invalidInvestigatorIds?.includes(investigator.communityId);
        })[investigatorIndex]

        makeApiCall({
            url: `/api/internal-affairs/complaint/${complaintId}/assign`,
            method: "POST",
            body: {
                assigneeCommunityId: investigator?.communityId
            },
            onLoadedCallback: () => {
                setAssignedInvestigators([...assignedInvestigators, {
                    nick: investigator.nick,
                    assigneeCommunityId: investigator.communityId
                }]);
                if (complaintStatus === "awaiting_investigator") {
                    setComplaintStatus("investigator_assigned")
                }
                setAlert("success", "Investigator assigned")
            },
            onError: () => {
                setAlert("error", "Investigated failed to update due to a issue")
            }
        })
    }

    const unAssignInvestigator = (complaintId: string, investigatorIndex: number) => {
        let investigator = assignedInvestigators[investigatorIndex]

        makeApiCall({
            url: `/api/internal-affairs/complaint/${complaintId}/unassign`,
            method: "POST",
            body: {
                assigneeCommunityId: investigator.assigneeCommunityId
            },
            onLoadedCallback: () => {
                setAlert("success", "Investigator un-assigned")
                assignedInvestigators.splice(investigatorIndex, 1)
            },
            onError: () => {
                setAlert("error", "Investigated failed to update due to a issue")
            }
        })
    }

    function Investigator(props: investigator) {
        let investigator = assignedInvestigators[props.investigatorIndex]

        return <Stack spacing={1} direction="row" alignItems="center">
            {props.complaint.complaintPermissions?.includes("assign") ? <HighlightOffOutlinedIcon color="error" fontSize="small" onClick={() => {
                if (props.complaint.complaintPermissions?.includes("assign")) {
                    unAssignInvestigator(props.complaint.displayId, props.investigatorIndex)
                }
            }}/> : <></>}
            <Typography variant="subtitle1"> {investigator.nick}</Typography>
        </Stack>
    }

    let invalidInvestigatorIds: Array<string> = assignedInvestigators.map(investigator => {
        return investigator.assigneeCommunityId
    })
    invalidInvestigatorIds.push(props.complaint.defendantCommunityId)
    invalidInvestigatorIds.push(props.complaint.creatorCommunityId)

    return <Stack spacing={1}>
        {IABanner}
        <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={8}>
                <Typography sx={{pt:2}} variant="h6" gutterBottom>Content and Evidence</Typography>
                <Paper variant={"outlined"} style={{
                    padding: "16px",
                }}>
                    <EditorContent content={props.complaint.content}/>
                </Paper>
                {statementView}
                <Typography sx={{pt:2}} variant="h6" gutterBottom>Complaint Conversation</Typography>
                <Stack sx={{mt:2}} spacing={1}>
                    {
                        complaintMessages?.map((message) => {
                            return <MessageBoxWithUserDetails key={message.complaintId}
                                authorCommunityId={message.authorCommunityId}
                                authorNick={message.authorNick}
                                authorRpName={message.authorRpName}
                                authorRankDisplayName={message.authorRankDisplayName}
                                authorRankColor={message.authorRankColor}
                                date={message.date}
                                content={message.content}
                            />
                        })
                    }
                    <Paper variant={"outlined"} style={{
                        padding: "16px"
                    }}>
                        {InternalAffairsComplaintStatus.get(complaintStatus ?? "closed")!!.blockReplies && !props.user.permissions?.includes("internalAffairs:extendedComplaintActions") ?
                            <>
                                <Typography>You do not have permission to reply to this complaint</Typography>
                            </> : <>
                                 <TextEditor ref={editorRef} initialContent={initialContent}/>
                                 <Button color="info" onClick={() => {
                                     if (editorRef.current) {
                                         editorRef.current.getEditorState().read(() => {
                                             let content = $generateHtmlFromNodes(editorRef.current!)

                                             editorRef.current?.update(() => {
                                                 $getRoot().clear();
                                             })

                                             makeApiCall({
                                                 url: `/api/internal-affairs/complaint/${props.complaint.displayId}/message`,
                                                 method: "POST",
                                                 body: {
                                                     messageHtml: content
                                                 },
                                                 onLoadedCallback: () => {
                                                     setAlert("success", "Message sent")
                                                     setComplaintMessages([...complaintMessages, {
                                                         complaintId: props.complaint.id,
                                                         authorCommunityId: props.user.communityId,
                                                         authorNick: props.user.nick,
                                                         authorRpName: props.user.rpName,
                                                         authorRankColor: props.user.rank?.color,
                                                         authorRankDisplayName: props.user.rank?.displayName,
                                                         content: content,
                                                         date: new Date().toString(),
                                                     }]);
                                                 },
                                                 onError: () => {
                                                     setAlert("error", "Message failed to send due to a issue")
                                                 }
                                             })
                                         })
                                     }
                                 }}>Send Message</Button>
                            </>
                        }
                    </Paper>
                </Stack>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={4}>
                <Typography sx={{pt:2}} variant="h6" gutterBottom>Complaint Management <Tooltip title="Click to Copy to Clipboard"><span style={{cursor: "pointer", color: "#1890ff"}} onClick={() => (navigator.clipboard.writeText(props.complaint.displayId))}>({props.complaint.displayId})</span></Tooltip></Typography>
                <Paper variant={"outlined"} style={{
                    padding: "16px"
                }}>
                    <Stack spacing={1}>
                        <Stack direction="row" alignItems="center">
                        <Typography variant="subtitle1">
                            Defendant - <Link underline="none" component={RouterLink} to={`/user/${props.complaint.defendantCommunityId}`}>
                                {props.complaint.defendantName}
                            </Link>
                            {(props.user.permissions?.includes("records:viewRecords") && props.complaint.complaintPermissions?.includes("assign")) && (
                                <Tooltip title="Records - Automatic Search">
                                <IconButton color="error" size="small"
                                    onClick={() => {
                                        makeApiCall({
                                            url: `/api/records/search/${props.complaint.defendantCommunityId}`,
                                            method: "POST",
                                            body: { reason: `IA Complaint ${props.complaint.displayId}` },
                                            onLoadedCallback: (data: {sessionId: string}) => {
                                                navigate(`/records/${props.complaint.defendantCommunityId}?session=${data.sessionId}`)
                                            },
                                            onError: () => {
                                                navigate(`/records/${props.complaint.defendantCommunityId}`)
                                            }
                                        })
                                    }}>
                                    <Policy/>
                                </IconButton>
                                </Tooltip>
                            )}
                        </Typography>
                        </Stack>
                        <Divider/>
                        {!props.complaint.anonymous ?
                           <Typography variant={"subtitle1"}>
                               Creator - <Link underline="none"

                                     component={RouterLink}
                                     to={`/user/${props.complaint.creatorCommunityId}`}
                               > {complaintCreator} </Link>
                               <Divider/>
                           </Typography> : <></>}
                        {props.complaint.anonymous && complaintCreator !== "Anonymous" ?
                            <Typography variant={"subtitle1"}>
                                Creator - <Link underline="none"

                                       component={RouterLink}
                                       to={`/user/${complaintCreatorCommunityId}`}
                                 > {complaintCreator} </Link>
                                <Divider/>
                            </Typography> : <></>}
                        <Typography variant="subtitle1">Created - {formatTimestamp(props.complaint.creationDate)}</Typography>
                        <Divider/>
                        <Typography variant="subtitle1">Outcome - {complaintAction}</Typography>
                        <Divider/>
                        <Typography variant="subtitle1">Assigned Investigators</Typography>
                        {
                            assignedInvestigators?.map((_, idx) => {
                                return <Investigator key={_.assigneeCommunityId} investigatorIndex={idx} complaint={props.complaint}/>
                            })
                        }
                        <Divider/>
                        <Typography variant="subtitle1">Status</Typography>
                        <Select
                            id="enter-status"
                            value={complaintStatus}
                            disabled={!props.complaint.complaintPermissions?.includes("edit_status") || complaintStatus === "final_reported" || !InternalAffairsComplaintStatus.get(complaintStatus ?? "closed")?.action}
                            onChange={(event) => {
                                updateStatus(props.complaint.displayId, event.target.value as string)
                            }}>
                            {
                                internalAffairsComplaintStatus.map(status => {
                                    return <MenuItem sx={{ display: `${(!status.action ||
                                        !props.user.permissions?.includes(status?.neededPermission ?? "internalAffairs:viewComplaint")) ? "none" : ""}` }}
                                                     key={status.key} value={status.key}>{status.displayName}</MenuItem>
                                })
                            }
                        </Select>
                        <Divider/>
                        <Typography variant="subtitle1">Classification</Typography>
                        <Select
                            id="enter-class"
                            value={complaintClassification}
                            disabled={
                                !props.complaint.complaintPermissions?.includes("classify") ||
                                ((InternalAffairsComplaintStatus.get(complaintStatus)?.statusOrder ?? -1) >= 5)
                            }
                            onChange={(event) => {
                                updateClassification(props.complaint.displayId, event.target.value as string)
                            }}>
                            {
                                internalAffairsComplaintClassification.map(classification => {
                                    return <MenuItem key={classification.key} value={classification.key}>{classification.displayName}</MenuItem>
                                })
                            }
                        </Select>
                        <Divider/>
                        {(props.complaint.complaintPermissions?.includes("assign")) ? <>
                            <Typography variant="subtitle1">Assign Investigator</Typography>
                            <Select
                                id="enter-inves"
                                value={selectedInvestigatorIndex}
                                disabled={
                                    !props.complaint.complaintPermissions?.includes("assign") ||
                                    (InternalAffairsComplaintStatus.get(complaintStatus)?.statusOrder ?? -1 >= 5) >= 5
                                }
                                onChange={(event) => {
                                    setSelectedInvestigatorIndex(event.target.value as number)
                                }}>
                                {
                                    props.investigators.filter(investigator => {
                                        return !invalidInvestigatorIds?.includes(investigator.communityId);
                                    })?.map((investigator, idx) => {
                                        return <MenuItem key={investigator.communityId}
                                                         value={idx}>{investigator.nick}</MenuItem>
                                    })
                                }
                            </Select>
                            <Button color="info" disabled={!props.complaint.complaintPermissions?.includes("assign") || ((InternalAffairsComplaintStatus.get(complaintStatus)?.statusOrder ?? -1) >= 5)} onClick={() => {
                                assignInvestigator(props.complaint.displayId, selectedInvestigatorIndex ?? -1)
                            }}>Assign</Button>
                            <Divider/>
                            <Typography variant="subtitle1">Due Date</Typography>
                            <TextField
                                type="datetime-local"
                                size="small"
                                value={complaintDueDate}
                                InputLabelProps={{shrink: true}}
                                onChange={(event) => {
                                    const value = event.target.value
                                    setComplaintDueDate(value)
                                    const dueDate = value ? new Date(value).toISOString() : null
                                    makeApiCall({
                                        url: `/api/internal-affairs/complaint/${props.complaint.displayId}/due-date`,
                                        method: "POST",
                                        body: { dueDate },
                                        onLoadedCallback: () => setAlert("success", "Due date updated"),
                                        onError: () => setAlert("error", "Failed to update due date")
                                    })
                                }}
                            />
                            <Divider/>
                        </> : <></>}
                        {props.complaint.complaintPermissions?.includes("submit_final_report") || props.complaint.complaintPermissions?.includes("view_final_report") ?
                            <>
                                {finalReportButton}
                            </>
                        : <></>}
                        {(props.user.permissions.includes("internalAffairs:viewComplaintCreator") && props.complaint.anonymous)?
                            <>
                                <Button color="warning" disabled={!props.complaint.complaintPermissions?.includes("assign")} onClick={() => {
                                    setViewHiddenIAWarning(true)
                                }}>View Complaint Creator</Button>
                                <Divider />
                            </>
                        : <></>}
                        <Button component={RouterLink} to={`/complaints`} color="info">Return</Button>
                    </Stack>
                </Paper>
               <Paper sx={{mt: 2}} variant={"outlined"} style={{padding: "16px"}}>
                    <Stack spacing={1}>
                        <Typography variant="subtitle1">Complaint Events</Typography>
                        <Timeline sx={{
                            [`& .${timelineItemClasses.root}:before`]: {
                                flex: 0,
                                padding: 0,
                            },
                        }}>
                            {props.complaint.activities?.map((activity) => (
                                <InternalAffairsComplaintEvent data={activity}/>
                            ))}
                        </Timeline>
                    </Stack>
                </Paper>
            </Grid>
        </Grid>
        <ConfirmationDialog title={"View Creator"} description={"This should only be used when investigating a situation wherein the IA system has been abused. This will create a permanent log, and the usage of this tool will be audited by the Advisory Board"} open={viewHiddenIAWarning} onClose={viewIACreator} />
        <Snackbar open={showAlert} autoHideDuration={5000} onClose={() => setShowAlert(false)}>
            <Alert severity={alertType} onClose={() => setShowAlert(false)} sx={{width: "100%"}} variant="filled">
                {alertText}
            </Alert>
        </Snackbar>
    </Stack>
}

export const InternalAffairsComplaintPage = () => {
    const params = useParams()
    const complaintData = useApiCall<InternalAffairsComplaintResponse>({
        url: `/api/internal-affairs/complaint/${params.id}`
    })

    const investigatorData = useApiCall<InternalAffairsComplaintInvestigatorResponse[]>({
        url: `/api/internal-affairs/investigators`
    })

    const userData = useUser()

    const user = userData.user

    const userDivisionRole = useApiCall<DivisionRoles>({
        url: `/api/user/${user?.communityId}/division-role/internal_affairs`
    })

    let pageContent

    if (complaintData.isError) {
        pageContent =
            <Alert severity="error">You cannot access this complaint, if this is a mistake contact the Professional
                Standards Department</Alert>
    } else if (complaintData.data && investigatorData.data && user) {
        pageContent =
            <Complaint userIARoleDisplayName={userDivisionRole.data?.displayName ?? ''}
                       user={user} complaint={complaintData.data}
                       investigators={investigatorData.data} complaintApiCall={complaintData}/>
    }

    return (
        <LoadingContent isLoading={
            complaintData.isLoading ||
            investigatorData.isLoading ||
            userDivisionRole.isLoading
        }>
            {pageContent}
        </LoadingContent>
    )
}
